export function formatWindowMillions(value) {
  const amount = Number(value) || 0;
  return `$${(amount / 1_000_000).toFixed(2)}M`;
}

const hiddenStages = new Set([
  'unspecified',
  'undefined',
  'on hold/nurture',
  'lost',
]);

export function visiblePipelineClients(clients) {
  return (clients || []).filter((client) => !client.hidden);
}

export function isDefinedPipelineStage(client) {
  if (client?.future) return true;
  const stage = String(client?.stage || '').trim().toLowerCase();
  return stage !== '' && !hiddenStages.has(stage);
}

export function customerHasFunding(customer) {
  return (customer?.months || []).some((month) => Number(month?.growthInvestments) > 0);
}

export function shownPipelineClient(client, tenantFilter) {
  if (!client || client.hidden) return false;
  if (!isDefinedPipelineStage(client)) return false;
  const tenant = String(client.tenant || '').trim();
  if (!tenantFilter || tenantFilter === 'Combined') {
    return tenant !== '' || !tenantFilter;
  }
  if (client.future && !tenant) return false;
  if (tenant && tenant !== tenantFilter) return false;
  return true;
}

const STAGE_ORDER = [
  'Live',
  'Future',
  'Confirmatory & Closing',
  'LOI/Term Sheet',
  'Underwriting',
  'Data collection',
  'Send data request',
  'Discovery',
  'Passed',
  'Stopped',
];

export function customerStage(customer, pipelineClients) {
  const pipeline = (pipelineClients || []).find((client) => client?.customerId === customer?.customerId);
  if (pipeline?.future) return 'Future';
  const stage = String(pipeline?.stage || '').trim();
  if (stage) return stage;
  return 'Live';
}

export function orderedStages(stages) {
  const unique = [...new Set((stages || []).map((stage) => String(stage || '').trim()).filter(Boolean))];
  return unique.sort((left, right) => {
    const leftIndex = STAGE_ORDER.indexOf(left);
    const rightIndex = STAGE_ORDER.indexOf(right);
    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });
}

export function shownChartCustomer(customer, pipelineClients, tenantFilter) {
  if (!customer?.customerId || !customerHasFunding(customer)) return false;
  const pipeline = (pipelineClients || []).find((client) => client?.customerId === customer.customerId);
  if (pipeline) return shownPipelineClient(pipeline, tenantFilter);
  const tenant = String(customer.tenant || '').trim();
  if (tenantFilter === 'Combined' && !tenant) return false;
  if (tenantFilter && tenantFilter !== 'Combined' && tenant !== tenantFilter) return false;
  return true;
}

export function hiddenPipelineClients(clients) {
  return (clients || []).filter((client) => client.hidden);
}

export function shiftMonth(month, delta) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(month || ''));
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + delta, 1));
  const year = date.getUTCFullYear();
  const nextMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${nextMonth}`;
}

export function commitmentMarketingMonths(client) {
  if (client?.future) {
    return (client.marketing || [])
      .filter((row) => Number(row?.spend) > 0)
      .map((row) => String(row.month || '').slice(0, 7))
      .filter((month) => /^\d{4}-\d{2}$/.test(month));
  }
  if (!Array.isArray(client?.savedMarketingMonths)) return null;
  return client.savedMarketingMonths;
}

const maxExtendedCohorts = 360;

function monthsBetween(start, end) {
  const left = /^(\d{4})-(\d{2})$/.exec(String(start || ''));
  const right = /^(\d{4})-(\d{2})$/.exec(String(end || ''));
  if (!left || !right) return null;
  return (Number(right[1]) - Number(left[1])) * 12 + (Number(right[2]) - Number(left[2]));
}

// Same rule as customer-service pipelineCohortsThrough: a short window is
// lengthened so the last cash month (cohort + lag) is the Through month.
export function cohortsThrough(firstCohortMonth, cohorts, lastFundingMonth, lagMonths) {
  const count = Math.max(0, Number(cohorts) || 0);
  const lag = Math.max(0, Number(lagMonths) || 0);
  const span = monthsBetween(shiftMonth(firstCohortMonth, lag), lastFundingMonth);
  if (span === null || span + 1 < 1) return count;
  return Math.max(count, Math.min(span + 1, maxExtendedCohorts));
}

// fillGrowthPercent is the monthly growth (in percent) used for months with no saved
// marketing: the default marketing growth once a default spend is set, otherwise 10%.
// Same rule as customer-service pipelineFillGrowth.
export function fillGrowthPercent(settings) {
  if (!hasDefaultMarketing(settings)) return 10;
  return liveMarketingGrowthPercent(Number(settings?.defaultMarketingGrowth) || 0);
}

function monthSpan(months) {
  if (!months.length) return null;
  return { count: months.length, from: months[0], through: months[months.length - 1] };
}

// marketingFill says which cohort months of a non-live or future customer will be
// funded from assumed marketing, using the same rule as customer-service
// pipelineFillMarketing:
//   continued: months after saved marketing (in the window, or the month just
//              before it), grown from the last saved month by the fill growth;
//   defaults:  months with no saved marketing before them, taken from the default
//              marketing schedule. noDefault is true when that schedule is not set,
//              so those months have no marketing and are not funded.
// Returns null when every month has saved marketing.
export function marketingFill(client, settings, fundingEndMonth) {
  const commitment = Number(client?.cohorts) || 0;
  const first = String(client?.firstFundingMonth || '');
  if (commitment <= 0 || !/^\d{4}-\d{2}$/.test(first)) return null;
  const months = commitmentMarketingMonths(client) || [];
  const extend = extendFundingEnabled(settings) && !!fundingEndMonth;
  const total = extend ? cohortsThrough(first, commitment, fundingEndMonth, client.lagMonths) : commitment;
  const saved = new Set(months.map((month) => String(month).slice(0, 7)));
  let hasLast = saved.has(shiftMonth(first, -1));
  let lastSaved = hasLast ? shiftMonth(first, -1) : '';
  const continued = [];
  const defaults = [];
  for (let index = 0; index < total; index += 1) {
    const month = shiftMonth(first, index);
    if (saved.has(month)) {
      hasLast = true;
      lastSaved = month;
    } else if (hasLast) {
      continued.push(month);
    } else {
      defaults.push(month);
    }
  }
  if (!continued.length && !defaults.length) return null;
  return {
    continued: monthSpan(continued),
    defaults: monthSpan(defaults),
    lastSaved,
    noDefault: defaults.length > 0 && !hasDefaultMarketing(settings),
  };
}

export function marketingFillSummary(fills, sectionLabel) {
  const people = (count) => `${count} ${sectionLabel} ${count === 1 ? 'customer' : 'customers'}`;
  const unfunded = fills.filter((fill) => fill?.noDefault).length;
  const defaults = fills.filter((fill) => fill?.defaults && !fill.noDefault).length;
  const continued = fills.filter((fill) => fill?.continued && !fill.defaults).length;
  const verb = (count, one, many) => (count === 1 ? one : many);
  const parts = [];
  if (defaults > 0) {
    parts.push(`${people(defaults)} ${verb(defaults, 'uses', 'use')} default marketing for months with no saved marketing.`);
  }
  if (continued > 0) {
    parts.push(`${people(continued)} ${verb(continued, 'grows past its', 'grow past their')} last saved marketing month.`);
  }
  if (unfunded > 0) {
    parts.push(`${people(unfunded)} ${verb(unfunded, 'has', 'have')} months with no marketing and no default marketing is set, so those months are not funded.`);
  }
  return parts.join(' ');
}

export function formatYearMonth(month) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(month || ''));
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function formatInstallmentDate(value) {
  const month = /^(\d{4})-(\d{2})$/.exec(String(value || ''));
  if (month) {
    const first = new Date(Date.UTC(Number(month[1]), Number(month[2]) - 1, 1));
    return first.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function liveMarketingGrowthFraction(value) {
  if (value === null || value === undefined || value === '') return 0.1;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0 || number > 100) return 0.1;
  if (number > 1) return number / 100;
  return number;
}

export function liveMarketingGrowthPercent(value) {
  return Math.round(liveMarketingGrowthFraction(value) * 1000) / 10;
}

// ownLiveMarketingGrowth is a live customer's own Marketing growth % as a fraction,
// or null when it is empty so the default marketing growth applies.
export function ownLiveMarketingGrowth(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0 || number > 100) return null;
  return number > 1 ? number / 100 : number;
}

export function hasDefaultMarketing(settings) {
  return Number(settings?.defaultMarketingSpend) > 0;
}

// customersMissingRepaymentCurve lists included non-live and future customers that
// cannot calculate returns: no repayment vector of their own, or Generic checked
// while the generic curve is empty. Save & Run refuses to start while any remain.
export function customersMissingRepaymentCurve(clients, settings) {
  const genericEmpty = (settings?.genericVector || []).length === 0;
  return (clients || []).filter((client) => {
    if (!client?.included) return false;
    if (client.useGenericCurve) return genericEmpty;
    return (client.repaymentVector || []).length === 0;
  });
}

export function utcYearMonth(date = new Date()) {
  return date.toISOString().slice(0, 7);
}

export function hasMarketingFromMonth(months, fromMonth) {
  return (months || []).some((row) => Number(row.spend) > 0 && String(row.month || '') >= fromMonth);
}

export const defaultMonthlyIrr = 0.014;

export function monthlyIrrOrDefault(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return defaultMonthlyIrr;
  return number;
}

export function pipelineClientInput(client, settings) {
  return {
    included: !!client.included,
    hidden: !!client.hidden,
    addedManually: !!client.addedManually,
    future: !!client.future,
    companyName: client.companyName || '',
    tenant: client.tenant || '',
    firstFundingMonth: client.firstFundingMonth || '',
    fundingPct: numberOrZero(client.fundingPct),
    monthlyIrr: monthlyIrrOrDefault(client.monthlyIrr),
    cohorts: Number(client.cohorts) || 0,
    maxCap: optionalNumber(client.maxCap),
    maxExposure: optionalNumber(client.maxExposure),
    useGenericCurve: !!client.useGenericCurve,
    marketingGrowth: client.marketingGrowth == null ? undefined : liveMarketingGrowthFraction(client.marketingGrowth),
    lagMonths: Number(client.lagMonths) || 0,
    repaymentVector: client.repaymentVector || [],
    marketing: (client.marketing || []).filter((row) => row && row.month),
  };
}

export function extendFundingEnabled(settings) {
  if (typeof settings?.extendFunding === 'boolean') return settings.extendFunding;
  return !!settings?.lastFundingMonth;
}

export function pipelineRecalcPayload(clients, settings, tenant) {
  return {
    tenant: tenant || '',
    settings: {
      spendPerMonth: numberOrZero(settings?.spendPerMonth),
      startMonth: settings?.startMonth || '',
      genericVector: settings?.genericVector || [],
      defaultMarketingSpend: numberOrZero(settings?.defaultMarketingSpend),
      defaultMarketingGrowth: numberOrZero(settings?.defaultMarketingGrowth),
      lastFundingMonth: settings?.lastFundingMonth || '',
      extendFunding: extendFundingEnabled(settings),
    },
    clients: (clients || []).map((client) => ({
      customerId: client.customerId,
      ...pipelineClientInput({ ...client, hidden: false }, settings),
    })),
  };
}

export function mergePipelineSeries(customers, customerId, series) {
  const rest = (customers || []).filter((customer) => customer.customerId !== customerId);
  if (!series) return rest;
  return [...rest, series];
}

function numberOrZero(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function optionalNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export { formatGroupedNumber, parseGroupedNumber } from 'components/groupedNumber';

export function consecutiveRoasText(points) {
  return [...(points || [])]
    .filter((point) => point && point.cumulativeRoasPct !== '' && point.cumulativeRoasPct !== null && point.cumulativeRoasPct !== undefined)
    .sort((left, right) => Number(left.month) - Number(right.month))
    .map((point) => point.cumulativeRoasPct)
    .join(', ');
}

export function parseConsecutiveRoas(text) {
  const trimmed = String(text ?? '').trim().replace(/^\[/, '').replace(/\]$/, '').trim();
  if (!trimmed) return { points: [], error: '' };
  const tokens = trimmed.split(/[\s,]+/).filter(Boolean);
  const points = [];
  for (const token of tokens) {
    const cleaned = token.endsWith('%') ? token.slice(0, -1) : token;
    if (!/^\d+(\.\d+)?$/.test(cleaned)) {
      return { points: [], error: `"${token}" is not a number` };
    }
    const cumulativeRoasPct = Number(cleaned);
    if (cumulativeRoasPct > 1000) {
      return { points: [], error: `"${token}" is out of range` };
    }
    points.push({ month: points.length, cumulativeRoasPct });
  }
  return { points, error: '' };
}
